import { ReactNode, useState } from "react"
import { Box, Card, Center, HStack, Skeleton, Text, VStack } from "@chakra-ui/react"
import { FaTrash } from "react-icons/fa6"
import Link from "@hydrologiq/shared/components/Link"
import { ScopeChip, ScopeLinkChip } from "@hydrologiq/shared/components/ScopeLinkChip"
import { formatAssetClass } from "@hydrologiq/shared/asset/PaginatedTable"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { IconButton } from "@chakra-ui/react"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { ConnectionStatus } from "@hydrologiq/shared/data-connections/constants"
import { BaseDataConnectionCard } from "@hydrologiq/shared/data-connections/BaseDataConnectionCard"
import { AlignedTitles, TitleWithChips } from "@hydrologiq/shared/components/TitleWithChips"
import {
  useAssetDataConnection,
  useRemoveAssetConnection,
  useAssetConnectionStatus,
} from "../../hooks/useAssetDataConnection"
import AssetSourceMapping from "./AssetSourceMapping"
import { DeleteDataConnectionDialog } from "@hydrologiq/shared/data-connections/DeleteDataConnectionDialog"
import AddAssetConnectionDialog from "./AddAssetConnectionDialog"

interface AssetDataConnectionProps {
  assetId: string | undefined
  /** The asset's class, used to hide incompatible connectors. */
  assetClass: string | null | undefined
  /** Shown in the delete confirmation. */
  assetName: string | null | undefined
  /**
   * Set when something else already monitors this asset, so it cannot hold its
   * own connection. Values: "system" or "installation".
   */
  monitoredElsewhere: "system" | "installation" | null
  /** Optional ID of the system this asset is assigned to. */
  systemId?: string | null
  /** Optional display name of the system this asset is assigned to. */
  systemName?: string | null
  /** Optional ID of the site this asset is assigned to. */
  siteId?: string | null
  /** Optional display name of the site this asset is assigned to. */
  siteName?: string | null
  /** Optional ID of the customer for the assigned site. */
  customerId?: string | null
  /** Optional display name of the customer. */
  customerName?: string | null
  /** Parent assets are excluded from this slice — connections go on modules. */
  isParent: boolean
  /**
   * Child modules of a parent/hybrid asset. Each child owns its own data
   * connection, managed on the child's own settings page — so the parent view
   * lists them with a link into each child.
   */
  childrenAssets?: Array<{
    id: string
    name?: string | null
    class?: string | null
    monitoring_status?: string | null
  }>
  /** Optional callback to select a tab in the parent view. */
  onSelectTab?: (tab: string) => void
  /** Whether the parent asset query is still loading. */
  loadingAsset?: boolean
}

export function AssetDataConnection({
  assetId,
  assetClass,
  assetName,
  monitoredElsewhere,
  systemId,
  systemName,
  siteId,
  siteName,
  customerId,
  customerName,
  isParent,
  childrenAssets = [],
  onSelectTab: _onSelectTab,
  loadingAsset = false,
}: AssetDataConnectionProps) {
  const { connection, loading, error, refetch } = useAssetDataConnection(isParent ? undefined : assetId)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  if (loading || loadingAsset) {
    return (
      <Center minH="100px">
        <Skeleton height="60px" width="100%" />
      </Center>
    )
  }

  const isComplexOrParent = isParent || assetClass?.toLowerCase() === "hybrid"

  if (isComplexOrParent) {
    return (
      <VStack align="stretch" gap={4} w="100%">
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="start" gap={2}>
            <Text as="div" fontSize="sm">
              This Parent Asset's modules own each of their data connections.
            </Text>
            {assetId && (
              <Text fontSize="sm">
                View and manage child modules on the{" "}
                <Link
                  href={`/assets/${assetId}?tab=modules`}
                  fontSize="sm"
                  fontWeight="semibold"
                  variant="primary"
                  external={true}
                >
                  Modules
                </Link>{" "}
                tab.
              </Text>
            )}
          </VStack>
        </Box>

        {childrenAssets.length > 0 && (
          // Every Module card's chips line up after the longest name, with a longer lead-in
          <AlignedTitles gap="3rem" display="flex" flexDirection="column" rowGap={3}>
            <Text fontSize="sm" fontWeight="semibold">
              Modules
            </Text>
            {childrenAssets.map((child) => (
              <ModuleConnection key={child.id} module={child} />
            ))}
          </AlignedTitles>
        )}
      </VStack>
    )
  }

  if (error) {
    return (
      <VStack align="stretch" gap={4} w="100%">
        <Text color="red.500">Failed to load data connections: {error}</Text>
      </VStack>
    )
  }

  // System membership. An asset that OWNS its connection keeps managing it here
  // — its own settings page is the only place its connection is manageable,
  // even when the asset is deployed into a system (the system shows it
  // read-only). We only lock this tab when the asset does NOT own a connection
  // but is monitored by a foreign system source.
  if ((monitoredElsewhere === "system" || systemId) && !connection) {
    return (
      <VStack align="stretch" gap={4} w="100%">
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="start" gap={2}>
            <Text as="div" fontSize="sm">
              This asset is part of a system:{" "}
              {systemId ? (
                <Link
                  href={`/systems/${systemId}/settings`}
                  fontSize="sm"
                  fontWeight="semibold"
                  variant="primary"
                  external={true}
                >
                  {systemName || "System"}
                </Link>
              ) : (
                <b>{systemName || "its system"}</b>
              )}
              .
            </Text>
            <Text fontSize="sm">
              The system owns the data connections. Manage data connections via the system.
            </Text>
          </VStack>
        </Box>
      </VStack>
    )
  }

  // Monitored through site installation
  if (monitoredElsewhere === "installation") {
    return (
      <VStack align="stretch" gap={4} w="100%">
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="start" gap={2}>
            <Text as="div" fontSize="sm">
              This asset is assigned to site{" "}
              {siteId && customerId ? (
                <Link
                  href={`/customers/${customerId}/sites/${siteId}`}
                  fontSize="sm"
                  fontWeight="semibold"
                  variant="primary"
                  external={true}
                >
                  {siteName || "Site"}
                </Link>
              ) : (
                <b>{siteName || "its site"}</b>
              )}
              {" "}and is monitored through its site installation, so it cannot hold its own data connection.
            </Text>
          </VStack>
        </Box>
      </VStack>
    )
  }

  if (!connection) {
    return (
      <VStack align="stretch" gap={4} w="100%">
        {siteId && customerId && (
          <Text as="div" fontSize="xs" color="fg.muted">
            The data connection will be assigned to site{" "}
            <b>{siteName || siteId}</b> (customer <b>{customerName || customerId}</b>).
          </Text>
        )}
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="start" gap={3}>
            <Text fontSize="sm" color="fg.muted">
              No data connection configured for this asset.
            </Text>
            <ActionButton
              context={{
                label: "Add new connection",
                description: "Add a new data connection",
                variant: "primary",
              }}
              onClick={() => setAddDialogOpen(true)}
            />
          </VStack>
        </Box>

        <AddAssetConnectionDialog
          isOpen={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          assetId={assetId!}
          assetClass={assetClass}
          onCreated={refetch}
        />
      </VStack>
    )
  }

  const inSystem = monitoredElsewhere === "system" || !!systemId

  return (
    <VStack align="stretch" gap={4} w="100%">
      {inSystem && (
        <Box borderWidth="1px" borderRadius="md" p={3} bg="bg.panel">
          <Text as="div" fontSize="sm">
            This asset is part of a system:{" "}
            {systemId ? (
              <Link
                href={`/systems/${systemId}/settings`}
                fontSize="sm"
                fontWeight="semibold"
                variant="primary"
                external={true}
              >
                {systemName || "System"}
              </Link>
            ) : (
              <b>{systemName || "its system"}</b>
            )}
            . Its connection is managed here on the asset and shown read-only in the system.
          </Text>
        </Box>
      )}
      {siteId && customerId && (
        <Text as="div" fontSize="xs" color="fg.muted">
          Assigned site:{" "}
          <Link
            href={`/customers/${customerId}/sites/${siteId}`}
            fontSize="xs"
            fontWeight="semibold"
            variant="primary"
          >
            {siteName || "Site"}
          </Link>
        </Text>
      )}
      <AssetConnectionCard
        connection={connection}
        assetId={assetId}
        assetClass={assetClass}
        assetName={assetName}
        onChanged={() => refetch()}
      />
    </VStack>
  )
}

type AssetConnection = NonNullable<ReturnType<typeof useAssetDataConnection>["connection"]>

interface AssetConnectionCardProps {
  connection: AssetConnection
  assetId: string | undefined
  assetClass: string | null | undefined
  /** Shown in the delete confirmation. */
  assetName: string | null | undefined
  onChanged: () => void
  /** Hide the mapping behind an expander, closed at first, as each module does on its parent. */
  collapsible?: boolean
  /** Heading in place of the connector name. */
  title?: ReactNode
  /** Chips beneath the title. */
  chips?: ReactNode
}

/**
 * An asset's own data connection: activate switch, source mapping and remove.
 * Used on the asset itself and for each module on its parent.
 */
function AssetConnectionCard({
  connection,
  assetId,
  assetClass,
  assetName,
  onChanged,
  collapsible = false,
  title,
  chips,
}: AssetConnectionCardProps) {
  const { removeConnection, removing } = useRemoveAssetConnection()
  const { setActive, updating } = useAssetConnectionStatus()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [hasMapping, setHasMapping] = useState(false)
  const isActive = connection.status === ConnectionStatus.Active

  return (
    <>
      <BaseDataConnectionCard
        connection={connection}
        collapsible={collapsible}
        defaultExpanded={!collapsible}
        title={title}
        chips={chips}
        actions={
          <>
            {!isActive && (
              <Tooltip content="Remove this data connection" positioning={{ placement: "top" }}>
                <Box display="inline-flex">
                  <IconButton
                    aria-label="Remove data connection"
                    variant={"hydrologiq_icon" as any}
                    size="sm"
                    disabled={removing}
                    onClick={() => setConfirmDelete(true)}
                  >
                    <FaTrash />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
            <Tooltip
              content="Map a data source before activating"
              disabled={isActive || hasMapping}
              positioning={{ placement: "top" }}
            >
              <Box display="inline-flex">
                <Switch
                  checked={isActive}
                  disabled={updating || removing || (!isActive && !hasMapping)}
                  onClick={async (e) => {
                    e.preventDefault()
                    if (!isActive && !hasMapping) return
                    try {
                      await setActive(connection.id, !isActive)
                      onChanged()
                    } catch (err: any) {
                      toaster.error({
                        title: "Failed to update the connection",
                        description: err?.message,
                      })
                    }
                  }}
                  inputProps={{
                    "aria-label": isActive ? "Deactivate connection" : "Activate connection",
                  }}
                />
              </Box>
            </Tooltip>
          </>
        }
      >
        {assetId && (
          <AssetSourceMapping
            monitoringInstanceId={connection.id}
            assetId={assetId}
            assetClass={assetClass}
            isActive={isActive}
            onChanged={onChanged}
            onMappingChange={setHasMapping}
          />
        )}
      </BaseDataConnectionCard>

      <DeleteDataConnectionDialog
        open={confirmDelete}
        loading={removing}
        title="Remove data connection"
        consequences={
          <Text fontSize="sm" color="fg.muted">
            Its source mapping will be removed and the asset will stop being
            monitored. This cannot be undone.
          </Text>
        }
        context={{
          connectorName: connection.connectorName || connection.connectorType,
          connectorType: connection.connectorType,
          externalId: connection.externalId,
          assetNames: assetName ? [assetName] : undefined,
        }}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          try {
            await removeConnection(connection.id)
            toaster.success({ title: "Data connection removed" })
            setConfirmDelete(false)
            onChanged()
          } catch (err: any) {
            toaster.error({
              title: "Failed to remove the data connection",
              description: err?.message,
            })
          }
        }}
      />
    </>
  )
}

interface ModuleConnectionProps {
  module: NonNullable<AssetDataConnectionProps["childrenAssets"]>[number]
}

/** One module of a parent asset, with its connection editable in place. */
function ModuleConnection({ module }: ModuleConnectionProps) {
  const { connection, loading, error, refetch } = useAssetDataConnection(module.id)

  const name = module.name || module.id
  const chips = (
    <>
      <ScopeLinkChip
        label="Module"
        href={`/assets/${module.id}/settings?tab=connections`}
        entityName={name}
      />
      {module.class && (
        <ScopeChip>{formatAssetClass(module.class)}</ScopeChip>
      )}
    </>
  )

  if (loading && !connection) return <Skeleton height="72px" width="100%" borderRadius="md" />

  if (connection) {
    return (
      <AssetConnectionCard
        connection={connection}
        assetId={module.id}
        assetClass={module.class}
        assetName={module.name}
        onChanged={() => refetch()}
        collapsible
        title={name}
        chips={chips}
      />
    )
  }

  return (
    <Card.Root variant="outline">
      <Card.Body p={4}>
        {/* Same left offset as a connection card's expander, so the chips line up */}
        <HStack align="start" gap={2}>
          <Box w={6} flexShrink={0} />
          <VStack align="stretch" gap={1} flex={1} minW={0}>
            <TitleWithChips title={name} chips={chips} />
            <Text fontSize="sm" color={error ? "red.500" : "fg.muted"}>
              {error ? `Failed to load data connection: ${error}` : "No data connection configured."}
            </Text>
          </VStack>
        </HStack>
      </Card.Body>
    </Card.Root>
  )
}

export default AssetDataConnection
